<template>
  <ResponsiveModal
    ref="modal"
    :title="title"
    :description="description"
    :withSearch="true"
    :confirmLabel="$t('components.buttons.save')"
    :showCancel="false"
    :showFooter="maxSmall"
    :disableConfirm="!selectedBuilding"
    @update:open="$emit('update:open', $event)"
  >
    <slot>
      <SelectRoot v-model="buildingId" required>
        <BuildingSelectTrigger :selected="selectedBuilding" />
      </SelectRoot>
    </slot>
    <template #content>
      <div
        class="grow p-4 pb-6 md:px-6 bg-secondary border-secondary md:border-t"
      >
        <Buildings
          :initialState="buildingId"
          @update:modelValue="handleUpdateBuildingId"
          :query="query"
        />
      </div>
    </template>
  </ResponsiveModal>
</template>

<script setup>
import { computed, ref, watchEffect, watch } from "vue";
import { storeToRefs } from "pinia";
import { SelectRoot } from "radix-vue";
import { BuildingSelectTrigger } from "@/SharedComponents/Select/Building";
import { Buildings } from "@/SharedComponents/Modals/Content";
import { ResponsiveModal } from "@/SharedComponents/ResponsiveModal";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";

const { maxSmall } = useBreakpoints();

const emit = defineEmits(["update:modelValue", "update:open"]);

const props = defineProps({
  modelValue: { type: Object, required: false },
  title: { type: String, required: false },
  description: { type: String, required: false },
  filterFn: { type: Function, required: false },
});

const globalFilterStore = useFilters("global");
const { filterValues } = storeToRefs(globalFilterStore);

const buildingId = ref(props.modelValue?.id || filterValues.value.tenant?.[0]);

const { isLoading, data } = useSites();

const buildings = computed(() => {
  if (isLoading.value) {
    return [];
  }
  if (props.filterFn) {
    return data.value.reduce((acc, building) => {
      if (props.filterFn(building)) {
        acc.push(building);
      }
      return acc;
    }, []);
  }
  return data.value;
});

const modal = ref(null);
const query = computed(() => modal.value?.query);

const selectedBuilding = computed(() =>
  buildings.value.find((building) => building.id === buildingId.value)
);

const handleUpdateBuildingId = (value) => {
  buildingId.value = value;

  if (buildingId.value) {
    modal.value.close();
  }
};

watch(props, () => {
  if (props.modelValue?.id) {
    buildingId.value = props.modelValue.id;
  }
});

watchEffect(() => {
  emit("update:modelValue", selectedBuilding.value);
});
</script>
